import React, {useEffect, useReducer, useState} from 'react';
import styled from 'styled-components';
import {ContentAdDesktop} from 'app/styles/content';
import {useDispatch, useSelector} from 'react-redux';
import {handleChangedDistrict} from 'app/actions/sessionActions';
import {LinkOnComponent} from 'app/components/links/link';
import {
  COLORS,
  SIZES,
  LoadingIndicator,
  List,
  ExpansionBody,
  ExpansionHeader,
  ToggleIcon,
  Placeholder,
} from '@fupa/fupa-uikit';
import {Helmet} from 'react-helmet-async';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import {useHistory, useLocation, useParams} from 'react-router';
import {selectDistrict} from 'app/hooks/reduxCreateSelectorHooks';
import {IconButton, InputAdornment, TextField} from '@mui/material';
import {fetchRegions, fetchDistricts, setRegionSelectHeader} from 'app/routes/region/RegionSelectPageActions';
import {RegionSelectPageReducer} from 'app/routes/region/RegionSelectPageReducer';
import {useFetchData, useRestoreData} from 'app/hooks/dataHooks';
import {Cancel} from '@mui/icons-material';
import {LastDistricts} from 'app/components/region/LastDistricts';
import {escapeRegExp} from 'app/helpers/helpers';

const District = styled(List)`
  padding-left: ${SIZES['52']};
`;

const FilterWrapper = styled.div`
  padding: 0.25rem 1rem;
`;

const RegionSelectPage = () => {
  // Keep path of caller (specific district page, e.g. MatchCalendarDefaultPage) to switch district
  const {state: caller} = useLocation();
  const history = useHistory();
  const districtSubPagePath = caller?.referrer ? `/${caller.referrer}` : '';

  const selectedUserDistrict = useSelector(selectDistrict);
  const {restoredData} = useRestoreData('RegionSelectPage');
  const {regionSlug} = useParams();
  const initialState = restoredData ?? {
    regions: {
      isFetching: false,
      items: [],
    },
    districts: {},
  };
  const [state, dispatch] = useReducer(RegionSelectPageReducer, initialState);
  const dispatchRedux = useDispatch();

  const loadDistrictsByRegion = slug => fetchDistricts(slug)(dispatch, state);

  const loadData = () => {
    fetchRegions(selectedUserDistrict, dispatchRedux)(dispatch, state);
    loadDistrictsByRegion(regionSlug);
  };

  const {
    regions: {items: regions},
    districts,
  } = state;
  useFetchData(state, loadData, 'RegionSelectPage');

  const defaultRegion = selectedUserDistrict?.region ?? null;
  const [openRegion, setOpenRegion] = useState(regionSlug);

  const defaultFilterValue = districts[regionSlug]
    ? districts[regionSlug].items?.[0]?.region?.name ?? ''
    : defaultRegion?.name ?? '';
  const [filterValue, setFilterValue] = useState(defaultFilterValue);

  useEffect(() => {
    if (!regionSlug && selectedUserDistrict?.region?.slug) {
      history.replace({pathname: `/select/${selectedUserDistrict.region.slug}`, state: caller});
      loadDistricts(selectedUserDistrict.region);
    }
  }, []);

  useEffect(() => {
    setRegionSelectHeader(dispatchRedux, selectedUserDistrict);
  }, [regionSlug]);

  useEffect(() => {
    if (openRegion === '') {
      history.replace({pathname: '/select', state: caller});
    }
  }, [openRegion]);

  const onFilterChange = event => {
    const value = event.target.value;
    setFilterValue(value);
  };

  const handleClear = () => {
    setFilterValue('');
  };

  const loadDistricts = region => {
    if (openRegion === region.slug) {
      setOpenRegion('');
    } else {
      loadDistrictsByRegion(region.slug);
      setOpenRegion(region.slug);
    }
  };

  const updateRegionSettings = ({
    brandings,
    ivw_st_desktop,
    ivw_st_mobil,
    ivw_dns_desktop,
    ivw_dns_mobil,
    ivw_cp_prefix,
    ...district
  }) => {
    handleChangedDistrict(
      dispatchRedux,
      district,
      brandings,
      ivw_st_desktop,
      ivw_st_mobil,
      ivw_dns_desktop,
      ivw_dns_mobil,
      ivw_cp_prefix,
      selectedUserDistrict
    );
  };

  const renderDistrict = district => {
    return (
      <LinkOnComponent
        enablehover={true.toString()}
        hovercolor={COLORS.lightGrey}
        to={`/region/${district.slug}${districtSubPagePath}`}
        key={district.slug}>
        <div onClick={() => updateRegionSettings(district)}>
          <District>{district.name}</District>
        </div>
      </LinkOnComponent>
    );
  };

  const renderRegion = region => {
    const open = region.slug === openRegion;
    let showDistricts;
    if (openRegion && openRegion in districts) {
      const districtRows = districts[openRegion].items.map(renderDistrict);
      showDistricts = districts[openRegion].isFetching ? (
        <LoadingIndicator />
      ) : districts[openRegion].items.length > 0 ? (
        districtRows
      ) : (
        <Placeholder>Es wurden leider keine Regionen gefunden.</Placeholder>
      );
    }

    return (
      <div key={region.slug}>
        <LinkOnComponent
          enablehover={true.toString()}
          replace
          to={{pathname: `/select/${region.slug}`, caller}}
          onClick={() => loadDistricts(region)}>
          <ExpansionHeader>
            <ToggleIcon addmargin='right' open={open} style={{color: COLORS.darkGrey}} />
            {region.name}
          </ExpansionHeader>
        </LinkOnComponent>
        <ExpansionBody
          height={
            open && districts[openRegion]
              ? districts[openRegion].items.length > 0
                ? districts[openRegion].items.length * 42
                : 42
              : 0
          }>
          {showDistricts}
        </ExpansionBody>
      </div>
    );
  };

  const visibleRegions = regions.filter(function (region) {
    return region.name.toLowerCase().search(escapeRegExp(filterValue.toLowerCase())) !== -1;
  });

  const canonical = regionSlug ? <link rel='canonical' href={`https://www.fupa.net/region/${regionSlug}`} /> : null;

  const elements = visibleRegions.map(renderRegion);

  const meta = [
    {
      name: 'robots',
      content: 'index',
    },
  ];

  return (
    <ContentAdDesktop noTabs top='4.5rem' noAds>
      <Helmet title={`Regionsauswahl${TITLE_POSTFIX}`} meta={meta}>
        {canonical}
      </Helmet>
      <LastDistricts selectedDistrict={selectedUserDistrict} />
      <FilterWrapper>
        <TextField
          autoFocus={true}
          color='secondary'
          fullWidth={true}
          id='filterregions'
          label='Filter Regionen'
          name='filterregions'
          autoComplete='off'
          onChange={onFilterChange}
          value={filterValue}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton tabIndex='1' aria-label='Textfeld zurücksetzen' onClick={handleClear} size='large'>
                  <Cancel style={{color: COLORS.darkGrey, fontSize: 24}} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FilterWrapper>
      {elements}
    </ContentAdDesktop>
  );
};

export default RegionSelectPage;
